import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, HelpCircle, Mail, MessageCircle, Wrench } from 'lucide-react';
import { Seo } from '../components/Seo';
import { LEGAL_CONTACT_EMAIL, LEGAL_ORG_NAME } from '../lib/legal-site-info';

export function ContactPage() {
  const mail = LEGAL_CONTACT_EMAIL.trim();
  const mailHref = mail ? `mailto:${mail}` : '';

  const quickLinks = [
    {
      to: '/help',
      label: 'Help & uitleg',
      desc: 'Stap-voor-stap: scan, account en downloads.',
      Icon: BookOpen,
    },
    {
      to: '/faq',
      label: 'Veelgestelde vragen',
      desc: 'Antwoorden op de meest voorkomende vragen.',
      Icon: HelpCircle,
    },
    {
      to: '/troubleshooting',
      label: 'Troubleshooting',
      desc: 'Iets werkt niet? Start hier met controleren.',
      Icon: Wrench,
    },
  ] as const;

  return (
    <>
      <Seo
        path="/contact"
        title="Contact"
        description={`Neem contact op met ${LEGAL_ORG_NAME} voor vragen over ComfortTool™, privacy, facturatie of samenwerking.`}
        keywords="ComfortTool contact, support, email, scan, account"
      />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.05fr] lg:items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-ct-text sm:text-4xl">Contact</h1>
            <p className="mt-4 text-base leading-relaxed text-ct-muted sm:text-lg">
              ComfortTool wordt aangeboden door <strong className="text-ct-text">{LEGAL_ORG_NAME}</strong>. We helpen je
              graag verder met vragen over de scan, je account, facturatie of privacy — het liefst per e-mail, zodat we
              je duidelijk en compleet kunnen antwoorden.
            </p>
            <div className="mt-8 flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/80 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ct-accent/12 text-ct-accent">
                <Clock className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-bold text-ct-text">Reactietijd</p>
                <p className="mt-1 text-sm leading-relaxed text-ct-muted">
                  We streven ernaar binnen enkele <strong className="text-ct-text">werkdagen</strong> te reageren. Spoed
                  in de woning? Schakel altijd een installateur of adviseur ter plaatse in — ComfortTool is een
                  indicatieve scan, geen spoedlijn.
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/80 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ct-accent/12 text-ct-accent">
                <MessageCircle className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-bold text-ct-text">Installatieadviseur</p>
                <p className="mt-1 text-sm leading-relaxed text-ct-muted">
                  Op veel pagina’s staat rechtsonder een icoon voor korte vragen over de scan en de app. Staat die uit,
                  gebruik dan de links hiernaast of mail ons.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {mail ? (
              <a
                href={mailHref}
                className="ct-hover-surface flex items-start gap-4 rounded-2xl border border-ct-border bg-ct-surface p-6 shadow-sm hover:border-ct-accent/40"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-ct-accent/15 text-ct-accent">
                  <Mail className="h-7 w-7" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wide text-ct-muted">E-mail</p>
                  <p className="mt-1 break-all text-lg font-semibold text-ct-accent">{mail}</p>
                  <p className="mt-2 text-sm text-ct-muted">
                    Vermeld bij voorkeur je e-mailadres van je account en kort waar het over gaat (bijv. “download
                    rapport”, “betaling”, “privacy”).
                  </p>
                </div>
              </a>
            ) : (
              <p className="rounded-2xl ct-caution-panel px-5 py-4 text-sm ct-caution-text">
                Stel <code className="rounded bg-ct-elevated px-1.5 py-0.5 text-xs text-ct-text">VITE_LEGAL_CONTACT_EMAIL</code>{' '}
                in voor het publieke adres.
              </p>
            )}

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-ct-muted">Snel verder</p>
              <ul className="mt-3 space-y-2">
                {quickLinks.map(({ to, label, desc, Icon }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="ct-hover-surface group flex items-center gap-3 rounded-xl border border-ct-border bg-ct-surface/90 px-4 py-3 transition hover:border-ct-accent/35"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ct-elevated text-ct-accent group-hover:bg-ct-accent/10">
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1 font-semibold text-ct-text">
                          {label}
                          <ArrowRight
                            className="h-4 w-4 text-ct-muted opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                            aria-hidden
                          />
                        </span>
                        <span className="mt-0.5 block text-xs text-ct-muted">{desc}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-ct-border bg-ct-elevated/40 px-5 py-4 text-sm text-ct-muted">
              <p>
                <Link to="/privacy" className="font-semibold text-ct-accent hover:underline">
                  Privacy
                </Link>
                {' · '}
                <Link to="/voorwaarden" className="font-semibold text-ct-accent hover:underline">
                  Algemene voorwaarden
                </Link>
                {' · '}
                <Link to="/bestelvoorwaarden" className="font-semibold text-ct-accent hover:underline">
                  Bestelvoorwaarden
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
